<script setup>
import { ref, computed, onMounted } from "vue";
import api from '@shop/frontend/web/js/api';
import { useCartDataProvider } from '@vueApp/hooks/useCartDataProvider.js';
import Popper from 'vue3-popper';
import useCartStore from '@vueApp/store/modules/cart.js';
import IPushkinWarningModal from '@components/IPushkinWarningModal/IPushkinWarningModal.vue';
import { translator } from '@shop/frontend/web/js/services/TranslatorService';

const cartStore = useCartStore();
const { isGuest, isFastBuy } = useCartDataProvider("cart-data-provider");

const props = defineProps({
  url: {
    type: String,
    default() {
      return '';
    },
  },
  options: {
    type: Object,
    default() {
      return {};
    },
  },
  smallBtn: {
    type: Boolean,
    default: false,
  },
  ticketsCount: {
    type: Number,
    default: 0,
  },
  firstEventId: {
    type: [Number, null],
    default: null,
  },
  eventType: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['setAcquiringError']);

const pushkinWarningModalShow = ref(false);
const isAcquiringPushkinEnable = props.options.payType === 'Pushkin';
const isPushkinRulesAccepted = ref(false);

const isPushkinPurchaseEnable = computed(() => {
  return props.ticketsCount === 1 && props.options.payType === 'Pushkin'
});

const isAcquiringPushkinOrCinema = ref(
  props.options.payType === 'Pushkin' || props.options.payType === 'Cinema',
);

const formatEventType = computed(() =>
  props.eventType === 'service_simple' ? 'service' : 'event'
);

const acquiringButtonClass = computed(() => {
  return {
    'js-get-fast-registration': isGuest,
    'i-acquirings-button_small': props.smallBtn,
    'i-acquirings-button_disabled': props.ticketsCount > 1 && isAcquiringPushkinOrCinema.value,
  };
});

const buttonPay = computed(() => {
  if (props.options.sum === 0) {
    return translator.t('main', 'Get a ticket');
  }
  return `${translator.t('main', 'Pay')} ${props.options.sum} ₽`;
});

function changePayAcquiring(acquiringId) {
  api.pay
    .changePayAcquiring({ orderId: props.options.orderId, acquiringId })
    .then((res) => {
      if (isGuest) {
        //TODO открыть модалку быстрой покупки
        return;
      }

      if ((props.options.payType === 'Pushkin' || acquiringId === 1) && !isPushkinRulesAccepted.value) {
        pushkinWarningModalShow.value = true;
        return;
      }

      return (window.location.href = props.url);
    })
    .catch(err => {
      emit('setAcquiringError', {
        isAcquiringError: true,
        errorMessage: err.message ?? '',
      });
    });
}

function payForOrder(event) {
  event?.preventDefault();
  const acquiringId = props.options.id;
  changePayAcquiring(acquiringId);

  if (isGuest) {
    event.preventDefault();
    cartStore.setCurrentOpenedEventId(props.firstEventId);
    cartStore.setFastBuyModalStatus(true);
    cartStore.setEventTypeToPay(formatEventType.value);
  }

}

function confirmPushkinRulesAndPay() {  
  isPushkinRulesAccepted.value = true;
  payForOrder();
}

</script>

<template>
  <button
    v-if="isGuest && !isFastBuy"
    class="btn btn_secondary i-acquirings-button"
    data-bs-toggle="modal"
    data-bs-target="#loginModal"
    data-test-id="login_acquiring_btn"
  >
    <slot>Авторизоваться для оплаты</slot>
  </button>
  <div v-else class="i-acquirings-button__link-wrapper">
    <a
      :href="url"
      class="btn btn_secondary i-acquirings-button"
      :class="acquiringButtonClass"
      data-test-id="acquiring_btn"
      @click="payForOrder($event)"
    >
      <slot>
        <template v-if="!options.name"> {{ buttonPay }} </template>
        <template v-else> {{ translator.t('main', options.name) }}</template>
      </slot>
    </a>
    <Popper
      v-if="isAcquiringPushkinOrCinema"
      class="light i-acquirings-button__popper"
      placement="top"
      arrow
      hover
      content="Вы можете оплатить заказ Пушкинской картой, только если в каждом мероприятии будет один билет."
    >
      <svg class="i-acquirings-button__pushkin-tooltip" width="12" height="12" fill="none">
        <use xlink:href="#attention" />
      </svg>
    </Popper>
  </div>
  <IPushkinWarningModal
    v-if="isAcquiringPushkinEnable"
    v-model="pushkinWarningModalShow"
    :is-pushkin-purchase-enable="isPushkinPurchaseEnable"
    @continue-buy="confirmPushkinRulesAndPay"
    @close-pushkin-warning-modal="pushkinWarningModalShow = false;"
  />
</template>

<style scoped lang="scss">
@import "frontend/web/src/styles/utils/variables.scss";
@import "./i-acquirings-button.scss";

:deep(.popper) {
  width: 280px;
  padding: 5px;
  border-radius: 6px;
  box-shadow: 0px 5px 5px rgba(0, 0, 0, 0.05);
  background-color: $white;
  color: $black;
  text-align: center;
  font-size: 14px;
  font-weight: 400;
}

:deep(.popper #arrow::before) {
  transform: translateX(-5px) rotate(45deg);
  background: $white;
}
</style>
